import smartDate from "@qrohlf/smart-date";
import cx from "classnames";
import { Icon } from "common/Icon";
import { CircleSpinner } from "common/spinners/CircleSpinner";
import { Card } from "common/styleguide/Card";
import { CTA } from "common/styleguide/CTA";
import { experienceGuidePageActions } from "data/resources/experienceGuidePages";
import { convertFromRaw, convertToRaw } from "draft-js";
import { editorStateFromDefault } from "lib/draftUtils";
import _ from "lodash";
import React, { Fragment } from "react";
import { connect } from "react-redux";
import styles from "./EditorColumn.scss";
import { GuidePageEditor } from "./GuidePageEditor";

export class _EditorColumn extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      savePending: false,
      saveSuccess: false,
      saveError: false,
      lastSave: null,
      lastSaveStr: "",
      title: props.selectedGuide ? props.selectedGuide.title : "",
      showDeleteDialog: false,
      editorState: null,
    };
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.selectedGuide ? prevProps.selectedGuide.id : null;
    const { selectedGuide } = this.props;
    const nextId = selectedGuide ? selectedGuide.id : null;

    if (prevId !== nextId) {
      const lastSave = selectedGuide ? { time: selectedGuide.updatedAt, auto: false } : null;
      this.setState({
        title: selectedGuide ? selectedGuide.title : "",
        lastSave,
      });
      this.getEditorState(true);
    }
  }

  componentDidMount() {
    // this interval is needed in order to force the `saved at` text to update.
    // (need to update state to rerun smartDate
    this._tickInterval = setInterval(this.tick, 30000);
  }

  componentWillUnmount() {
    clearInterval(this.tick);
  }

  tick = () => {
    const { lastSave } = this.state;
    this.setState({
      lastSaveStr: lastSave ? smartDate(lastSave.time, { includeTime: false, relative: "day" }) : "",
    });
  };

  inflateEditorStateFromContent = _.memoize((content) =>
    editorStateFromDefault(content ? convertFromRaw(content) : null)
  );

  // lazy-load editorState
  getEditorState = (bust = false) => {
    const { selectedGuide } = this.props;

    if (!selectedGuide) {
      return this.setState({ editorState: null });
    }

    if (bust) {
      this.setState({ editorState: null });
      return this.inflateEditorStateFromContent(selectedGuide.content);
    } else {
      return this.state.editorState || this.inflateEditorStateFromContent(selectedGuide.content);
    }
  };

  onSave = (isAutosave = true) => {
    const { updateGuidePage, selectedGuide } = this.props;
    const { title } = this.state;

    if (!selectedGuide) {
      return;
    }

    const rawContentState = convertToRaw(this.getEditorState().getCurrentContent());

    this.setState(
      {
        savePending: true,
        saveSuccess: false,
        saveError: false,
        lastSave: { time: new Date(), auto: isAutosave },
      },
      () =>
        updateGuidePage(selectedGuide.set("content", rawContentState).set("title", title))
          .then(() => {
            this.setState({
              savePending: false,
              saveSuccess: true,
            });
          })
          .catch(() =>
            this.setState({
              savePending: false,
              saveError: true,
            })
          )
    );
  };

  // autosave every second
  debouncedAutoSave = _.debounce(this.onSave, 1000, {
    leading: false,
    trailing: true,
  });

  onTitleChange = ({ title }) => {
    this.setState({ title });
    // skip autosave if selectedGuide is live
    if (!this.props.selectedGuide.isLive) {
      this.debouncedAutoSave();
    }
  };

  onEditorChange = (editorState, cb) => {
    this.setState({ editorState }, cb);
    // skip autosave if selectedGuide is live
    if (!this.props.selectedGuide.isLive) {
      this.debouncedAutoSave();
    }
  };

  render() {
    const { guides, selectedGuide } = this.props;
    const { title, savePending, lastSave, lastSaveStr } = this.state;

    return (
      <div className={styles.EditorColumn}>
        {!selectedGuide && <EmptyEditor guides={guides} />}
        {selectedGuide && (
          <Fragment>
            <Card className={styles.editorCard}>
              {lastSave && (
                <div className={styles.savedAt}>
                  {lastSave.auto && "auto"}saved{" "}
                  {lastSaveStr ||
                    smartDate(lastSave.time, {
                      includeTime: false,
                      relative: "day",
                    })}
                </div>
              )}

              <div className={styles.title}>{selectedGuide.isHeader ? "Header " : "Section "}title</div>
              <div className={cx(styles.guideTitle, selectedGuide.isHeader && styles.header)}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    this.onSave(false);
                  }}
                >
                  <input
                    type="text"
                    value={title}
                    placeholder="(Untitled)"
                    onChange={(e) => this.onTitleChange({ title: e.target.value })}
                    className={styles.titleInput}
                  />

                  {/* 104px is the natural width of the button when displaying 'Save' */}
                  {selectedGuide.isLive && (
                    <CTA button inline type="submit" style={{ width: "104px" }} className={styles.liveSaveButton}>
                      {savePending ? <CircleSpinner size={15} /> : "Save"}
                    </CTA>
                  )}
                </form>
              </div>
              {!selectedGuide.isHeader && (
                <GuidePageEditor
                  guide={selectedGuide}
                  editorState={this.getEditorState()}
                  onChange={this.onEditorChange}
                  onSave={this.onSave}
                  onCancel={this.onCancel}
                />
              )}
            </Card>
          </Fragment>
        )}
      </div>
    );
  }
}

const EmptyEditor = ({ guides }) => (
  <Card className={styles.emptyEditor}>
    <h1>{guides.size > 0 ? "Select" : "Add a"} guide section to edit</h1>
    <Icon i="md-map" size={120} />
  </Card>
);

const mapStateToProps = null;

const mapDispatchToProps = {
  updateGuidePage: experienceGuidePageActions.update,
};

export const EditorColumn = connect(mapStateToProps, mapDispatchToProps)(_EditorColumn);
